import PropTypes from 'prop-types';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

const AuthContext = createContext(undefined);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({
  children,
  initialUser = null,
  initialGroups = [],
  initialSubmissions = [],
  initialEvents = [],
  initialUserData = null,
  initialCsrfToken = '',
  initialIsVerified = false,
}) => {
  const [user, setUser] = useState(initialUser);
  const [groups, setGroups] = useState(initialGroups);
  const [submissions, setSubmissions] = useState(initialSubmissions);
  const [events, setEvents] = useState(initialEvents);
  const [userData, setUserData] = useState(initialUserData);
  const [csrfToken, setToken] = useState(initialCsrfToken);
  const [isVerified, setVerified] = useState(Boolean(initialIsVerified));
  const [loading, setLoading] = useState(initialUser === null);

  // IF SSR FAILS TO LOAD USER BEFORE HYDRATION, THIS WILL UPDATE PROPS WHEN IT DETECTS A USER
  // THIS ALSO HELPS WITH LOCAL DEV NOT LOADING USER WHEN PAGE RELOADS FROM CHANGES
  // TODO: ADD THIS FIX TO MAIN WEBSITE AUTHCONTEXT
  useEffect(() => {
    if (!initialUser) return;
    setUser(initialUser);
    setGroups(initialGroups);
    setSubmissions(initialSubmissions);
    setEvents(initialEvents);
    setUserData(initialUserData);
    setToken(initialCsrfToken);
    setVerified(Boolean(initialIsVerified));
    setLoading(false);
  }, [initialUser]);

  const value = useMemo(
    () => ({
      user,
      userData,
      groups,
      submissions,
      events,
      csrfToken,
      isVerified,
      loading,
    }),
    [user, userData, groups, submissions, events, csrfToken, isVerified, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialUser: PropTypes.object,
  initialGroups: PropTypes.arrayOf(PropTypes.string),
  initialSubmissions: PropTypes.arrayOf(PropTypes.object),
  initialEvents: PropTypes.arrayOf(PropTypes.object),
  initialUserData: PropTypes.object,
  initialCsrfToken: PropTypes.string,
  initialIsVerified: PropTypes.bool,
};

export default AuthContext;
